import { Tabs } from 'antd';
import { FileX, ShieldAlert } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentUser } from '../../../shared/features/session/components/CurrentUserProvider';
import { DetailPageSkeleton } from '../../../shared/components/DetailPageSkeleton';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageHeader } from '../../../shared/components/PageHeader';
import { DriverAvailabilityPanel } from '../components/DriverAvailabilityPanel';
import { DriverDetailsForm } from '../components/DriverDetailsForm';
import { DriverDocuments } from '../components/DriverDocuments';
import { DriverHistoryTab } from '../components/DriverHistoryTab';
import { DriverStatusControl } from '../components/DriverStatusControl';
import { DRIVER_TAB_LABELS, DRIVER_TABS, driverTabPath, type DriverTab } from '../drivers.paths';
import { useDriverDetail } from '../hooks/use-driver-detail';
import { withinSection } from '../../../shared/components/section-trail';

/**
 * A driver's own page: fields and the not-yet-available placeholders, documents and history, each
 * on its own tab like a truck's. Personal data (national ID, date of birth) is behind `driver.view` itself, the
 * access-restriction CLAUDE.md → Data protection asks for — the list screen is what excludes it.
 */
export function DriverDetailPage({ tab }: { tab: DriverTab }) {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { state, updateError, saving, update, changeStatus, uploadDocument } = useDriverDetail(id);
  const { state: currentUser } = useCurrentUser();
  const canManage = currentUser.status === 'authenticated' && currentUser.user.permissions.includes('driver.manage');

  if (state.status === 'forbidden') return <Navigate to="/" replace />;

  if (state.status === 'loading') return <DetailPageSkeleton label="Loading driver" breadcrumbs={withinSection('drivers')} tabs={DRIVER_TABS.length} />;

  if (state.status === 'not_found') {
    return <EmptyState icon={FileX} title="Driver not found" description="This driver doesn't exist, or the link is out of date." />;
  }

  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load this driver" description="Something went wrong loading this driver. Try reloading the page." />;
  }

  const { driver } = state;

  return (
    <>
      <PageHeader
        breadcrumbs={withinSection('drivers')}
        title={driver.name}
        description={driver.phone}
        actions={<DriverStatusControl driver={driver} canManage={canManage} onChangeStatus={changeStatus} />}
      />
      <Tabs
        activeKey={tab}
        onChange={(key) => navigate(driverTabPath(driver.id, key as DriverTab))}
        items={[
          {
            key: 'details',
            label: DRIVER_TAB_LABELS.details,
            children: (
              <div className="flex flex-col gap-8">
                <DriverAvailabilityPanel availability={driver.availability} />
                <DriverDetailsForm driver={driver} canManage={canManage} error={updateError} saving={saving} onSave={update} />
                <DriverPlaceholders />
              </div>
            ),
          },
          {
            key: 'documents',
            label: DRIVER_TAB_LABELS.documents,
            children: <DriverDocuments driverId={driver.id} documents={driver.documents} canManage={canManage} onUpload={uploadDocument} />,
          },
          { key: 'history', label: DRIVER_TAB_LABELS.history, children: <DriverHistoryTab driverId={driver.id} /> },
        ]}
      />
    </>
  );
}

/** Advance balance (M6) and behaviour score (M5) aren't built yet — shown as unavailable rather than a guessed shape those milestones might not match. */
function DriverPlaceholders() {
  return (
    <section className="grid grid-cols-2 gap-3">
      <div className="rounded-lg border border-line bg-surface px-4 py-3">
        <p className="text-sm font-medium text-fg">Advance balance</p>
        <p className="text-sm text-muted">Not yet available</p>
      </div>
      <div className="rounded-lg border border-line bg-surface px-4 py-3">
        <p className="text-sm font-medium text-fg">Behaviour score</p>
        <p className="text-sm text-muted">Not yet available</p>
      </div>
    </section>
  );
}
